"use client";

import React from "react";
import { Wrench, Flame, Gauge, ShieldCheck, Zap, MapPin, Clock, Award, Phone, MessageCircle, CircleQuestionMark as HelpCircle, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
const technicalServices = [{
  icon: Flame,
  title: "إصلاح أعطال الإشعال الذاتي",
  desc: "فحص واستبدال شمعات الإشعال ومولد الشرارة وصمامات التدفق لكافة أفران الغاز والكهرباء بدقة متناهية."
}, {
  icon: Wrench,
  title: "معالجة تسريب مفاتيح التحكم",
  desc: "صيانة مفاتيح الغاز وتبديل الجلود المانعة للتسرب وضبط مقاييس الأمان لضمان سلامة الاستخدام المنزلي."
}, {
  icon: Gauge,
  title: "وزن وموازنة مستوى الشعلة",
  desc: "تنظيف الفواني وتعديل ضغط الغاز وتوزيع اللهب بالتساوي للحصول على تسوية متكاملة وخالية من الهباب."
}, {
  icon: Zap,
  title: "معايرة وضبط الثرموستات",
  desc: "فحص حساسات الحرارة ومنظم الدرجات للأفران الكهربائية والغازية لتفادي الاحتراق وتحقيق الاستقرار الحراري."
}, {
  icon: ShieldCheck,
  title: "فحص وتأمين حساسات الأمان",
  desc: "اختبار صمام الأمان والمزدوج الحراري (Thermocouple) لقطع تدفق الغاز فوريًا عند انطفاء الشعلة المفاجئ."
}];
const whyChooseUs = [{
  icon: Award,
  title: "خبرة في جميع الماركات",
  desc: "معرفة هندسية متخصصة بصيانة الأفران الإيطالية، التركية، والمحلية بمختلف تقنياتها وموديلاتها."
}, {
  icon: Wrench,
  title: "فنيون مهنيون معتمدون",
  desc: "طاقم فني مدرب على أعلى معايير السلامة المهنية وتشخيص الأعطال المستعصية مباشرة بالمنزل."
}, {
  icon: Clock,
  title: "خدمة منزلية سريعة بالرياض",
  desc: "وصول سريع لجميع أحياء ومناطق الرياض دون الحاجة لنقل الفرن خارج مطبخك إطلاقاً."
}, {
  icon: ShieldCheck,
  title: "ضمان شامل ومعتمد",
  desc: "ضمان حقيقي على كافة قطع الغيار الأصلية المستبدلة وعلى جودة العمل والصيانة المنفذة."
}];
const faqItems = [{
  question: "كم تستغرق مدة زيارة وإصلاح الفرن بالمنزل عادة؟",
  answer: "تستغرق زيارة الصيانة والفحص الشامل في الغالب ما بين 40 إلى 60 دقيقة لمعظم الأعطال الشائعة، حيث يتم الفحص والإصلاح والاختبار النهائي فوراً داخل منزلك دون نقل الجهاز."
}, {
  question: "هل توفرون قطع غيار أصلية للأفران الغازية والكهربائية؟",
  answer: "نعم، نوفر قطع غيار أصلية ومطابقة لمواصفات المصنع لمختلف الماركات العالمية (مثل مفاتيح التحكم، الحساسات، شمعات الإشعال، الزجاج الحراري، والمقابض) مع شهادة ضمان معتمدة."
}, {
  question: "ما هي تفاصيل وفترة الضمان المقدمة على الصيانة؟",
  answer: "نقدم ضماناً معتمداً يغطي قطع الغيار المستبدلة وأجور اليد العاملة. في حال ظهور أي خلل مرتبط بالصيانة خلال فترة الضمان، يتم التعامل معه ومتابعته مجاناً وفوراً."
}, {
  question: "كيف يمكنني حجز موعد لصيانة الفرن لدى لمسة احتراف؟",
  answer: `يمكنك حجز الموعد بخطوة واحدة عبر الاتصال المباشر على الرقم ${PHONE_LOCAL} أو مراسلتنا عبر الواتساب مع تحديد نوع العطل والحي السكني داخل الرياض لتأكيد أقرب موعد متاح.`
}];
export default function OvenMaintenanceServiceOverview() {
  return <section id="ovenmaintenanceserviceoverview" data-nav="light" dir="rtl" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Block 1: Service Scope & Technical Diagnostics */}
        <div className="mb-16 md:mb-20">
          <div className="max-w-3xl mb-10">
            <div className="w-8 h-[3px] bg-primary mb-4 rounded-full" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
              خدمات صيانة الأفران الشاملة بالرياض
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              تشخيص تقني دقيق وحلول معتمدة لكافة أعطال الأفران الغازية والكهربائية المنزلية مع الالتزام بأعلى معايير السلامة والأداء المثالي.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {technicalServices.map((service, index) => {
            const Icon = service.icon;
            return <Card key={index} data-index={index} className="border border-border bg-card shadow-xs hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="flex flex-row items-center gap-4 space-y-0 pb-2">
                    <div className="size-12 rounded-lg bg-muted border border-border flex items-center justify-center shrink-0 text-primary">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {service.desc}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Block 2: Why Choose لمسة احتراف */}
        <div className="mb-16 md:mb-20">
          <div className="max-w-3xl mb-10">
            <div className="w-8 h-[3px] bg-primary mb-4 rounded-full" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
              لماذا تختار صيانة لمسة احتراف؟
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              نضمن لك سرعة الإنجاز، الكفاءة الهندسية المتقدمة، وقطع الغيار الموثوقة مع الحفاظ التام على أمان منزلك.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
            const Icon = item.icon;
            return <div key={index} data-index={index} className="p-6 rounded-xl border border-border bg-card shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="size-12 rounded-full border border-border bg-muted/60 flex items-center justify-center mb-5 text-primary">
                      <Icon className="size-6" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Block 3: Service Coverage in Riyadh */}
        <div className="mb-16 md:mb-20">
          <div className="rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-3 max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-background border border-border text-xs font-semibold text-primary">
                  <MapPin className="size-3.5 text-primary" />
                  <span>تغطية متكاملة لمدينة الرياض</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  خدمة منزلية فورية في كافة أحياء الرياض
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  تصل فرق الصيانة المتنقلة التابعة لشركة لمسة احتراف إلى منزلك في شمال وشرق وغرب وجنوب الرياض، مجهزين بكامل المعدات وقطع الغيار المعتمدة دون الحاجة لنقل الفرن.
                </p>
              </div>

              <div className="flex flex-wrap gap-2 md:justify-end">
                {["شمال الرياض", "شرق الرياض", "وسط وغرب الرياض", "جنوب الرياض"].map((zone, idx) => <span key={idx} data-index={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-card border border-border text-foreground">
                      <CheckCircle2 className="size-3.5 text-primary" />
                      {zone}
                    </span>)}
              </div>
            </div>
          </div>
        </div>

        {/* Block 4: Frequently Asked Questions */}
        <div className="mb-16 md:mb-20">
          <div className="max-w-3xl mb-8">
            <div className="w-8 h-[3px] bg-primary mb-4 rounded-full" />
            <div className="inline-flex items-center gap-2 text-primary font-medium text-sm mb-2">
              <HelpCircle className="size-4" />
              <span>الأسئلة الشائعة</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              إجابات شاملة حول خدمة صيانة الأفران
            </h2>
          </div>

          <div className="max-w-4xl border border-border rounded-xl bg-card p-4 sm:p-6 shadow-xs">
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((faq, index) => <AccordionItem key={index} data-index={index} value={`faq-${index}`} className="border-b border-border/80 last:border-b-0 py-1">
                  <AccordionTrigger className="text-right text-base font-semibold text-foreground hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {/* Block 5: Call to Action */}
        <div className="rounded-2xl border border-border bg-card p-8 sm:p-10 md:p-12 shadow-sm text-center relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-4">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              هل تواجه عطلاً في فرنك الغازي أو الكهربائي؟
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              تواصل مع فنيي لمسة احتراف الآن للحصول على فحص منزلي مباشر، معالجة فورية، وضمان كامل على كافة الأعمال.
            </p>

            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 text-base font-medium shadow-xs">
                <Link to={WHATSAPP_HREF} newTab>
                  <MessageCircle className="size-5 ml-2" />
                  حجز موعد عبر واتساب
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-accent text-base font-medium shadow-xs">
                <a href={PHONE_TEL_HREF}>
                  <Phone className="size-5 ml-2 text-primary" />
                  اتصال مباشر: {PHONE_LOCAL}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}